/**
 * Settings Page
 * =============
 * Account settings page with password change, avatar configuration, and preferences
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../contexts/ToastContext';
import { Card, Input, Button, Alert, Badge } from '../components/ui';
import { DashboardLayout } from '../components/layout';
import { usePermissions } from '../hooks/usePermissions';
import authAPI from '../api/auth';
import {
  getAvatarConfigs,
  createAvatarConfig,
  updateAvatarConfig,
  deleteAvatarConfig,
} from '../api/proposals';

// Default values for the avatar form
const DEFAULT_AVATAR_FORM = {
  name: '',
  gender: 'male',
  anam_avatar_id: '',
  tts_voice_id: '',
  tts_speed: 'normal',
  tts_speed_alpha: 1.0,
  tts_model: 'cartesia/sonic-3',
  llm_model: 'gpt-4o-mini',
  image_url: '',
  is_active: true,
};

const Settings = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { isAdmin } = usePermissions();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('password');

  // Password state
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [passwordErrors, setPasswordErrors] = useState({});
  const [passwordStrength, setPasswordStrength] = useState(0);

  // Avatar configuration state
  const [avatarConfigs, setAvatarConfigs] = useState([]);
  const [avatarLoading, setAvatarLoading] = useState(false);
  const [avatarFormVisible, setAvatarFormVisible] = useState(false);
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
  const [avatarForm, setAvatarForm] = useState({ ...DEFAULT_AVATAR_FORM });
  const [editingConfigId, setEditingConfigId] = useState(null);
  const [avatarSaving, setAvatarSaving] = useState(false);

  // Load avatar configs on mount (admin-only feature)
  const loadAvatarConfigs = useCallback(async () => {
    setAvatarLoading(true);
    try {
      const response = await getAvatarConfigs();
      setAvatarConfigs(response.data || []);
    } catch (error) {
      console.error('Failed to load avatar configs:', error);
      showToast('Failed to load presenter configurations', 'error');
    } finally {
      setAvatarLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    if (isAdmin) {
      loadAvatarConfigs();
    }
  }, [isAdmin, loadAvatarConfigs]);

  // --- Password handlers ---

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm((prev) => ({ ...prev, [name]: value }));

    if (passwordErrors[name]) {
      setPasswordErrors((prev) => ({ ...prev, [name]: '' }));
    }

    if (name === 'new_password') {
      calculatePasswordStrength(value);
    }
  };

  const calculatePasswordStrength = (password) => {
    if (!password) {
      setPasswordStrength(0);
      return;
    }

    let strength = 0;
    if (password.length >= 12) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[!@#$%^&*]/.test(password)) strength++;

    setPasswordStrength(strength);
  };

  const validatePasswordForm = () => {
    const newErrors = {};

    if (!passwordForm.current_password) {
      newErrors.current_password = 'Current password is required';
    }
    if (!passwordForm.new_password) {
      newErrors.new_password = 'New password is required';
    }
    if (!passwordForm.confirm_password) {
      newErrors.confirm_password = 'Please confirm your new password';
    }

    if (passwordForm.new_password) {
      if (passwordForm.new_password.length < 12) {
        newErrors.new_password = 'Password must be at least 12 characters';
      } else if (!/[A-Z]/.test(passwordForm.new_password)) {
        newErrors.new_password = 'Password must contain uppercase letter';
      } else if (!/[a-z]/.test(passwordForm.new_password)) {
        newErrors.new_password = 'Password must contain lowercase letter';
      } else if (!/[0-9]/.test(passwordForm.new_password)) {
        newErrors.new_password = 'Password must contain number';
      } else if (!/[!@#$%^&*]/.test(passwordForm.new_password)) {
        newErrors.new_password = 'Password must contain special character';
      }
    }

    if (passwordForm.new_password !== passwordForm.confirm_password) {
      newErrors.confirm_password = 'Passwords do not match';
    }

    if (passwordForm.new_password === passwordForm.current_password) {
      newErrors.new_password =
        'New password must be different from current password';
    }

    setPasswordErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    if (!validatePasswordForm()) {
      return;
    }

    setLoading(true);

    try {
      await authAPI.changePassword(
        passwordForm.current_password,
        passwordForm.new_password,
        passwordForm.confirm_password
      );

      showToast(
        'Password changed successfully. Please log in again.',
        'success'
      );

      setPasswordForm({
        current_password: '',
        new_password: '',
        confirm_password: '',
      });
      setPasswordStrength(0);

      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (error) {
      console.error('Password change error:', error);

      const errorMessage =
        error.response?.data?.detail?.error?.message ||
        error.response?.data?.message ||
        'Failed to change password';
      showToast(errorMessage, 'error');

      const fieldErrors =
        error.response?.data?.detail?.error?.details ||
        error.response?.data?.errors;
      if (fieldErrors) {
        setPasswordErrors(fieldErrors);
      }
    } finally {
      setLoading(false);
    }
  };

  const isPasswordValid = passwordStrength >= 5;

  // --- Avatar handlers ---

  const handleAvatarFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setAvatarForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };


  const openAddForm = () => {
    setAvatarForm({ ...DEFAULT_AVATAR_FORM });
    setEditingConfigId(null);
    setAvatarFormVisible(true);
  };

  const openEditForm = (config) => {
    setAvatarForm({
      name: config.name || '',
      gender: config.gender || 'male',
      anam_avatar_id: config.anam_avatar_id || '',
      tts_voice_id: config.tts_voice_id || '',
      tts_speed: config.tts_speed || 'normal',
      tts_speed_alpha: config.tts_speed_alpha ?? 1.0,
      tts_model: config.tts_model || 'cartesia/sonic-3',
      llm_model: config.llm_model || 'gpt-4o-mini',
      image_url: config.image_url || '',
      is_active: config.is_active || false,
    });
    setEditingConfigId(config.id);
    setAvatarFormVisible(true);
  };

  const cancelAvatarForm = () => {
    setAvatarFormVisible(false);
    setEditingConfigId(null);
    setAvatarForm({ ...DEFAULT_AVATAR_FORM });
  };

  const handleAvatarSubmit = async (e) => {
    e.preventDefault();
    setAvatarSaving(true);

    try {
      if (editingConfigId) {
        await updateAvatarConfig(editingConfigId, avatarForm);
        showToast('Presenter configuration updated successfully', 'success');
      } else {
        await createAvatarConfig(avatarForm);
        showToast('Presenter configuration created successfully', 'success');
      }

      cancelAvatarForm();
      await loadAvatarConfigs();
    } catch (error) {
      console.error('Avatar config save error:', error);
      const errorMessage =
        error.response?.data?.detail?.error?.message ||
        error.response?.data?.message ||
        'Failed to save presenter configuration';
      showToast(errorMessage, 'error');
    } finally {
      setAvatarSaving(false);
    }
  };

  const handleDeleteConfig = async (configId) => {
    if (!window.confirm('Are you sure you want to delete this presenter configuration?')) {
      return;
    }

    try {
      await deleteAvatarConfig(configId);
      showToast('Presenter configuration deleted', 'success');
      await loadAvatarConfigs();
    } catch (error) {
      console.error('Avatar config delete error:', error);
      const errorMessage =
        error.message ||
        'Failed to delete presenter configuration';
      showToast(errorMessage, 'error');
    }
  };

  const handleActivateConfig = async (configId) => {
    try {
      await updateAvatarConfig(configId, { is_active: true });
      showToast('Presenter configuration activated', 'success');
      await loadAvatarConfigs();
    } catch (error) {
      console.error('Avatar config activate error:', error);
      const errorMessage =
        error.response?.data?.detail?.error?.message ||
        error.response?.data?.message ||
        'Failed to activate presenter configuration';
      showToast(errorMessage, 'error');
    }
  };

  const handleDeactivateConfig = async (configId) => {
    if (!window.confirm('Are you sure you want to deactivate this presenter? It will no longer be available for assignment.')) {
      return;
    }
    try {
      await updateAvatarConfig(configId, { is_active: false });
      showToast('Presenter configuration deactivated', 'success');
      await loadAvatarConfigs();
    } catch (error) {
      console.error('Avatar config deactivate error:', error);
      const errorMessage =
        error.message ||
        'Failed to deactivate presenter configuration';
      showToast(errorMessage, 'error');
    }
  };

  const tabs = [
    { id: 'password', label: 'Change Password' },
    ...(isAdmin ? [{ id: 'presenter', label: 'Presenter Configuration' }] : []),
  ];

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-[28px] font-sans font-bold text-gray-900 dark:text-white">
            Settings
          </h1>
          <p className="text-gray-600 mt-2">
            Manage your account security and preferences
          </p>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="flex gap-6">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`pb-3 text-sm font-medium transition-colors border-b-2 ${
                  activeTab === tab.id
                    ? 'text-primary border-primary'
                    : 'text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Change Password Tab */}
        {activeTab === 'password' && (
        <Card className="mb-8">
          <div className="p-6 sm:p-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">
              Change Password
            </h2>

            <form onSubmit={handlePasswordSubmit} className="space-y-6">
              <div className="max-w-2xl">
                <div className="space-y-5">
                  <Input
                    label="Current Password"
                    type="password"
                    name="current_password"
                    value={passwordForm.current_password}
                    onChange={handlePasswordChange}
                    error={passwordErrors.current_password}
                    required
                    autoComplete="current-password"
                  />

                  <Input
                    label="New Password"
                    type="password"
                    name="new_password"
                    value={passwordForm.new_password}
                    onChange={handlePasswordChange}
                    error={passwordErrors.new_password}
                    required
                    autoComplete="new-password"
                    helpText="Minimum 12 characters"
                  />

                  <Input
                    label="Confirm New Password"
                    type="password"
                    name="confirm_password"
                    value={passwordForm.confirm_password}
                    onChange={handlePasswordChange}
                    error={passwordErrors.confirm_password}
                    required
                    autoComplete="new-password"
                  />
                </div>

                {/* Password Strength Indicator */}
                {passwordForm.new_password && (
                  <div className="mt-6 p-5 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex justify-between items-center mb-3">
                      <span className="text-sm font-medium text-gray-700">
                        Password Strength
                      </span>
                      <span
                        className={`text-sm font-semibold ${
                          passwordStrength <= 2
                            ? 'text-red-600'
                            : passwordStrength <= 3
                            ? 'text-yellow-600'
                            : 'text-green-600'
                        }`}
                      >
                        {passwordStrength <= 2
                          ? 'Weak'
                          : passwordStrength <= 3
                          ? 'Medium'
                          : 'Strong'}
                      </span>
                    </div>
                    <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden mb-5">
                      <div
                        className={`h-full transition-all duration-300 ${
                          passwordStrength <= 2
                            ? 'bg-red-500'
                            : passwordStrength <= 3
                            ? 'bg-yellow-500'
                            : 'bg-green-500'
                        }`}
                        style={{ width: `${(passwordStrength / 5) * 100}%` }}
                      />
                    </div>

                    {/* Password Requirements */}
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-3">
                        Password Requirements:
                      </p>
                      <ul className="space-y-2">
                        <li
                          className={`text-sm flex items-center gap-2 ${
                            passwordForm.new_password.length >= 12
                              ? 'text-green-600 font-medium'
                              : 'text-gray-600'
                          }`}
                        >
                          <span className="text-lg">
                            {passwordForm.new_password.length >= 12 ? '✓' : '○'}
                          </span>
                          <span>Minimum 12 characters</span>
                        </li>
                        <li
                          className={`text-sm flex items-center gap-2 ${
                            /[A-Z]/.test(passwordForm.new_password)
                              ? 'text-green-600 font-medium'
                              : 'text-gray-600'
                          }`}
                        >
                          <span className="text-lg">
                            {/[A-Z]/.test(passwordForm.new_password) ? '✓' : '○'}
                          </span>
                          <span>At least one uppercase letter (A-Z)</span>
                        </li>
                        <li
                          className={`text-sm flex items-center gap-2 ${
                            /[a-z]/.test(passwordForm.new_password)
                              ? 'text-green-600 font-medium'
                              : 'text-gray-600'
                          }`}
                        >
                          <span className="text-lg">
                            {/[a-z]/.test(passwordForm.new_password) ? '✓' : '○'}
                          </span>
                          <span>At least one lowercase letter (a-z)</span>
                        </li>
                        <li
                          className={`text-sm flex items-center gap-2 ${
                            /[0-9]/.test(passwordForm.new_password)
                              ? 'text-green-600 font-medium'
                              : 'text-gray-600'
                          }`}
                        >
                          <span className="text-lg">
                            {/[0-9]/.test(passwordForm.new_password) ? '✓' : '○'}
                          </span>
                          <span>At least one number (0-9)</span>
                        </li>
                        <li
                          className={`text-sm flex items-center gap-2 ${
                            /[!@#$%^&*]/.test(passwordForm.new_password)
                              ? 'text-green-600 font-medium'
                              : 'text-gray-600'
                          }`}
                        >
                          <span className="text-lg">
                            {/[!@#$%^&*]/.test(passwordForm.new_password)
                              ? '✓'
                              : '○'}
                          </span>
                          <span>At least one special character (!@#$%^&*)</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                )}

                <div className="mt-6">
                  <Alert variant="warning">
                    After changing your password, you will be logged out and need
                    to log in again with your new password.
                  </Alert>
                </div>
              </div>

              <div className="pt-4 border-t border-gray-200">
                <Button
                  type="submit"
                  loading={loading}
                  disabled={
                    !passwordForm.current_password ||
                    !passwordForm.new_password ||
                    !passwordForm.confirm_password
                  }
                >
                  Change Password
                </Button>
              </div>
            </form>
          </div>
        </Card>
        )}

        {/* Presenter Configuration Tab (admin only) */}
        {activeTab === 'presenter' && isAdmin && (
        <Card className="mb-8">
          <div className="p-6 sm:p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">
                Presenter Configuration
              </h2>
              {!avatarFormVisible && (
                <Button onClick={openAddForm}>
                  Add Presenter
                </Button>
              )}
            </div>

            {/* Avatar Form (Add / Edit) */}
            {avatarFormVisible && (
              <div className="mb-8 p-6 bg-gray-50 rounded-lg border border-gray-200">
                <h3 className="text-lg font-medium text-gray-900 mb-5">
                  {editingConfigId ? 'Edit Presenter' : 'Add New Presenter'}
                </h3>

                <form onSubmit={handleAvatarSubmit} className="space-y-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <Input
                      label="Name"
                      name="name"
                      value={avatarForm.name}
                      onChange={handleAvatarFormChange}
                      required
                      placeholder="e.g. Professional Male"
                    />

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Gender
                      </label>
                      <div className="flex items-center gap-6 mt-1">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="gender"
                            value="male"
                            checked={avatarForm.gender === 'male'}
                            onChange={handleAvatarFormChange}
                            className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-700">Male</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="gender"
                            value="female"
                            checked={avatarForm.gender === 'female'}
                            onChange={handleAvatarFormChange}
                            className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-700">Female</span>
                        </label>
                      </div>
                    </div>

                    <Input
                      label="Avatar Replica ID"
                      name="anam_avatar_id"
                      value={avatarForm.anam_avatar_id}
                      onChange={handleAvatarFormChange}
                      required
                      placeholder="Tavus replica ID (e.g. re6220ec0195)"
                    />

                    <Input
                      label="TTS Voice ID"
                      name="tts_voice_id"
                      value={avatarForm.tts_voice_id}
                      onChange={handleAvatarFormChange}
                      required
                      placeholder="Voice ID for TTS"
                    />

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        TTS Speed
                      </label>
                      <select
                        name="tts_speed"
                        value={`${avatarForm.tts_speed}|${Number(avatarForm.tts_speed_alpha).toFixed(1)}`}
                        onChange={(e) => {
                          const [speed, alpha] = e.target.value.split('|');
                          setAvatarForm((prev) => ({ ...prev, tts_speed: speed, tts_speed_alpha: parseFloat(alpha) }));
                        }}
                        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="slow|1.2">Very Slow</option>
                        <option value="slow|1.0">Slow</option>
                        <option value="normal|1.3">Relaxed</option>
                        <option value="normal|1.0">Normal</option>
                        <option value="normal|0.8">Conversational</option>
                        <option value="fast|1.0">Fast</option>
                        <option value="fast|0.8">Very Fast</option>
                      </select>
                    </div>

                    <Input
                      label="TTS Model"
                      name="tts_model"
                      value={avatarForm.tts_model}
                      onChange={handleAvatarFormChange}
                      placeholder="cartesia/sonic-3"
                    />

                    <Input
                      label="LLM Model"
                      name="llm_model"
                      value={avatarForm.llm_model}
                      onChange={handleAvatarFormChange}
                      placeholder="gpt-4o-mini"
                    />
                  </div>

                  <Input
                    label="Presenter Image URL"
                    name="image_url"
                    value={avatarForm.image_url || ''}
                    onChange={handleAvatarFormChange}
                    placeholder="https://example.com/avatar-preview.png"
                  />
                  {avatarForm.image_url && (
                    <div className="flex items-center gap-3">
                      <img src={avatarForm.image_url} alt="Presenter preview" className="w-12 h-12 rounded-full object-cover border border-gray-200 cursor-pointer hover:opacity-80 transition-opacity" onClick={() => setImagePreviewUrl(avatarForm.image_url)} />
                      <span className="text-xs text-gray-500">Click to preview</span>
                    </div>
                  )}

                  <div className="flex items-center gap-3 pt-4 border-t border-gray-200">
                    <Button type="submit" loading={avatarSaving} disabled={!avatarForm.name}>
                      {editingConfigId ? 'Update Presenter' : 'Save Presenter'}
                    </Button>
                    <button
                      type="button"
                      onClick={cancelAvatarForm}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Avatar List */}
            {avatarLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-3 text-gray-500">Loading configurations...</span>
              </div>
            ) : avatarConfigs.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <p className="text-lg font-medium mb-1">No presenter configurations yet</p>
                <p className="text-sm">Add your first presenter to enable presentation mode.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {avatarConfigs.map((config) => (
                  <div
                    key={config.id}
                    className={`rounded-lg border p-5 ${
                      config.is_active
                        ? 'border-green-300 bg-green-50'
                        : 'border-gray-200 bg-white'
                    }`}
                  >
                    {/* Header row */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        {config.image_url ? (
                          <img src={config.image_url} alt={config.name} className="w-10 h-10 rounded-full object-cover border-2 border-gray-200 cursor-pointer hover:opacity-80 transition-opacity" onClick={() => setImagePreviewUrl(config.image_url)} />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-400">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
                            </svg>
                          </div>
                        )}
                        <div>
                          <h3 className="text-base font-semibold text-gray-900">
                            {config.name}
                          </h3>
                          <Badge variant={config.gender === 'female' ? 'accent' : 'primary'}>
                            {config.gender}
                          </Badge>
                        </div>
                      </div>
                      {config.is_active ? (
                        <Badge variant="success">Active</Badge>
                      ) : (
                        <Badge variant="default">Inactive</Badge>
                      )}
                    </div>

                    {/* Details */}
                    <div className="space-y-1 text-sm text-gray-600 mb-4">
                      <p>
                        <span className="font-medium text-gray-700">Speed:</span>{' '}
                        {(() => {
                          const s = config.tts_speed;
                          const a = Number(config.tts_speed_alpha || 1);
                          if (s === 'slow' && a >= 1.2) return 'Very Slow';
                          if (s === 'slow') return 'Slow';
                          if (s === 'normal' && a >= 1.2) return 'Relaxed';
                          if (s === 'normal' && a <= 0.8) return 'Conversational';
                          if (s === 'fast' && a <= 0.8) return 'Very Fast';
                          if (s === 'fast') return 'Fast';
                          return 'Normal';
                        })()}
                      </p>
                      <p>
                        <span className="font-medium text-gray-700">LLM Model:</span>{' '}
                        {config.llm_model || 'gpt-4o-mini'}
                      </p>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 pt-3 border-t border-gray-200">
                      {config.is_active ? (
                        <button
                          onClick={() => handleDeactivateConfig(config.id)}
                          className="px-3 py-1.5 text-xs font-medium text-yellow-700 bg-yellow-100 rounded-md hover:bg-yellow-200 transition-colors"
                        >
                          Deactivate
                        </button>
                      ) : (
                        <button
                          onClick={() => handleActivateConfig(config.id)}
                          className="px-3 py-1.5 text-xs font-medium text-green-700 bg-green-100 rounded-md hover:bg-green-200 transition-colors"
                        >
                          Activate
                        </button>
                      )}
                      <button
                        onClick={() => openEditForm(config)}
                        className="px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-100 rounded-md hover:bg-blue-200 transition-colors"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteConfig(config.id)}
                        className="px-3 py-1.5 text-xs font-medium text-red-700 bg-red-100 rounded-md hover:bg-red-200 transition-colors"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>
        )}
      </div>
      {/* Image Preview Modal */}
      {imagePreviewUrl && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => setImagePreviewUrl(null)}>
          <div className="relative" onClick={(e) => e.stopPropagation()}>
            <img src={imagePreviewUrl} alt="Presenter" className="max-w-md max-h-[80vh] rounded-xl shadow-2xl object-contain" />
            <button
              onClick={() => setImagePreviewUrl(null)}
              className="absolute -top-3 -right-3 w-8 h-8 bg-white rounded-full shadow-lg flex items-center justify-center text-gray-500 hover:text-gray-700"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default Settings;
